
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import { ensureValidToken } from "@/services/AuthAwareApiService";

export const useFundResearch = () => {
  const [fundName, setFundName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const { userRole, isLoading: roleLoading } = useUserRole();

  const handleResearch = async () => {
    if (roleLoading) {
      toast.error("Please wait while we verify your permissions");
      return;
    }

    if (!userRole) {
      toast.error("You don't have permission to perform research");
      return;
    }

    if (!fundName && !pdfFile) {
      toast.error("Please either enter a fund name or upload a PDF");
      return;
    }

    setIsLoading(true);
    try {
      console.log('Starting fund research for:', fundName || 'PDF upload');

      let pdfContent = null;
      if (pdfFile) {
        console.log('Processing PDF file:', pdfFile.name);
        const fileName = `${Date.now()}-${pdfFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('fund_documents')
          .upload(fileName, pdfFile);

        if (uploadError) {
          console.error('PDF upload error:', uploadError);
          throw uploadError;
        }

        console.log('PDF uploaded successfully:', uploadData);

        const { data: { publicUrl } } = supabase.storage
          .from('fund_documents')
          .getPublicUrl(fileName);

        pdfContent = publicUrl;
        console.log('PDF public URL:', pdfContent);
      }

      console.log('Sending research request:', { 
        fundName: fundName.trim(),
        pdfUrl: pdfContent
      });

      // Ensure valid token before making API call
      await ensureValidToken('research-fund');

      const { data, error } = await supabase.functions.invoke("research-fund", {
        body: { 
          fundName: fundName.trim(),
          pdfUrl: pdfContent
        }
      });

      if (error) {
        console.error('Research function error:', error);
        throw error;
      }

      console.log('Research function response:', data);

      if (!data?.csvData) {
        console.error('No CSV data in response');
        throw new Error('No data returned from research function');
      }

      // Create performance CSV file
      const performanceBlob = new Blob([data.csvData.performance], { type: 'text/csv' });
      const performanceUrl = URL.createObjectURL(performanceBlob);
      const performanceLink = document.createElement('a');
      performanceLink.href = performanceUrl;
      performanceLink.download = `${fundName.trim() || 'fund'}-performance.csv`;
      performanceLink.click();

      // Create calendar returns CSV file
      const returnsBlob = new Blob([data.csvData.calendarReturns], { type: 'text/csv' });
      const returnsUrl = URL.createObjectURL(returnsBlob);
      const returnsLink = document.createElement('a');
      returnsLink.href = returnsUrl;
      returnsLink.download = `${fundName.trim() || 'fund'}-calendar-returns.csv`;
      returnsLink.click();

      toast.success("Fund data has been downloaded as CSV files. You can now analyze and import them.", {
        className: "bg-[#F2FCE2] border-green-200 text-green-800",
      });
    } catch (error: any) {
      console.error('Research error:', error);
      toast.error(error.message || "Failed to research fund");
    } finally {
      setIsLoading(false);
      setPdfFile(null);
    }
  };

  return {
    fundName,
    setFundName,
    isLoading,
    pdfFile,
    setPdfFile,
    handleResearch
  };
};
